import type { ApprovalGateKind, MetricCard } from '../projections.ts';
import { Btn, StatusPill } from '../primitives.tsx';
import { blockedActivationReason } from './metric-activation-copy.ts';

export interface MetricProposalGateProps {
  readonly metric: MetricCard;
  readonly busy: boolean;
  readonly onRoute: (kind: ApprovalGateKind, objectId: string) => void;
  readonly onSetup: () => void;
  readonly onDelete: () => void;
  readonly confirmDelete: boolean;
  readonly onConfirmDelete: () => void;
  readonly onCancelDelete: () => void;
}

function DeleteControls(props: MetricProposalGateProps): React.JSX.Element {
  if (!props.confirmDelete) {
    return (
      <Btn
        sm
        kind="quiet"
        disabled={props.busy}
        ariaLabel={`Delete KPI: ${props.metric.name}`}
        onClick={props.onConfirmDelete}
      >
        Delete metric
      </Btn>
    );
  }
  return (
    <span className="g-act">
      <Btn sm kind="ghost" disabled={props.busy} onClick={props.onCancelDelete}>
        Cancel
      </Btn>
      <Btn
        sm
        disabled={props.busy}
        ariaLabel={`Permanently delete KPI: ${props.metric.name}`}
        onClick={props.onDelete}
      >
        Permanently delete
      </Btn>
    </span>
  );
}

function BlockedProposal(props: MetricProposalGateProps): React.JSX.Element {
  const questions = props.metric.clarificationQuestions;
  return (
    <div className="md-gate" data-activation-blocked="true">
      <span className="g-note">
        {props.confirmDelete
          ? 'This permanently removes the unactivated proposal and its draft setup.'
          : blockedActivationReason(props.metric.activationReadiness)}
        {!props.confirmDelete && questions.length > 0 ? (
          <span data-clarification-questions> {questions.join(' ')}</span>
        ) : null}
      </span>
      <span className="g-act">
        {props.confirmDelete ? null : <StatusPill tone="attention">Setup required</StatusPill>}
        {props.confirmDelete ? null : (
          <Btn sm kind="ghost" disabled={props.busy} onClick={props.onSetup}>
            Finish setup
          </Btn>
        )}
        <DeleteControls {...props} />
      </span>
    </div>
  );
}

/** Actionable setup/activation/delete controls for one unactivated Metric proposal. */
export function MetricProposalGate(props: MetricProposalGateProps): React.JSX.Element {
  if (props.metric.activationReadiness?.ready !== true) return <BlockedProposal {...props} />;
  return (
    <div className="md-gate">
      <span className="g-note">
        Proposed as a tracked KPI — activating starts collecting and charting it.
      </span>
      <span className="g-act">
        <Btn
          sm
          disabled={props.busy}
          ariaLabel={`Activate KPI: ${props.metric.name}`}
          onClick={() => props.onRoute('metric_activation', props.metric.id)}
        >
          Activate
        </Btn>
        <DeleteControls {...props} />
      </span>
    </div>
  );
}
